// Sheep weigh-ins module-level cache. Mirrors cattleCache.js for sheep.
// Each sheep sub-view (Home / Flocks / Batches / Weigh-Ins) needs the
// full species-scoped weigh_ins list. 30s TTL, invalidated after any
// write (tag reconcile, session delete, send-to-processor, etc.).
import {wcfSelectAll} from './pagination.js';
import type {SupabaseErrorLike, SupabaseClient} from './supabaseTypes.js';

// `select('*')` returns whole weigh_ins rows and callers consume the rest of
// the columns, so only the one column THIS module reads is modelled. The rest
// stay `unknown` rather than being invented into a schema TypeScript cannot
// verify. `entered_at` is optional because the sort below already defends
// against it being absent — the type follows the code, not the other way round.
export type WeighInRow = {entered_at?: string | null; [column: string]: unknown};

// A weigh_in_sessions row as narrowed by `.select('id')`. weigh_in_sessions.id
// is `text NOT NULL` (archive/001_cattle_module.sql).
type SessionIdRow = {id: string};

export type CacheOptions = {throwOnError?: boolean};

// The route-resource catalog needs the same uncached, signal-aware primitive
// as cattleCache. The module cache below is intentionally retained for the
// legacy sheep surfaces; it is not safe as a route resource because its rows
// outlive auth/query scope and it cannot cancel an in-flight read.
type MaybeAbortable<Builder> = {abortSignal?: (signal: AbortSignal) => Builder};

export async function loadSheepWeighIns(
  sb: SupabaseClient,
  {signal}: {signal?: AbortSignal} = {},
): Promise<WeighInRow[]> {
  const withSignal = <Builder extends MaybeAbortable<Builder>>(b: Builder): Builder =>
    signal && b && typeof b.abortSignal === 'function' ? b.abortSignal(signal) : b;

  const sR = await withSignal(sb.from('weigh_in_sessions').select('id').eq('species', 'sheep'));
  if (sR.error) throw new Error('sheep weigh-in sessions: ' + (sR.error.message || sR.error));

  const ids = (sR.data || []).map((s: SessionIdRow) => s.id);
  if (ids.length === 0) return [];

  const pageErrorSlot: SupabaseErrorLike[] = [];
  const rows = await wcfSelectAll<WeighInRow>(async (f, t) => {
    const res = await withSignal(sb.from('weigh_ins').select('*').in('session_id', ids).range(f, t));
    if (res.error) pageErrorSlot.push(res.error);
    return res;
  });
  const pageError = pageErrorSlot.at(-1);
  if (pageError) throw new Error('sheep weigh_ins: ' + (pageError.message || pageError));

  rows.sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || ''));
  return rows;
}

let _sheepWeighInsCache: WeighInRow[] | null = null;
let _sheepWeighInsCacheAt = 0;
const SHEEP_WEIGH_INS_TTL_MS = 30000;

export async function loadSheepWeighInsCached(sb: SupabaseClient, opts: CacheOptions = {}): Promise<WeighInRow[]> {
  const throwOnError = !!opts.throwOnError;
  if (_sheepWeighInsCache && Date.now() - _sheepWeighInsCacheAt < SHEEP_WEIGH_INS_TTL_MS) {
    return _sheepWeighInsCache;
  }
  const sR = await sb.from('weigh_in_sessions').select('id').eq('species', 'sheep');
  if (sR.error) {
    if (throwOnError) throw new Error('loadSheepWeighInsCached sessions: ' + (sR.error.message || sR.error));
    return _sheepWeighInsCache || [];
  }
  const ids = (sR.data || []).map((s: SessionIdRow) => s.id);
  if (ids.length === 0) {
    _sheepWeighInsCache = [];
    _sheepWeighInsCacheAt = Date.now();
    return _sheepWeighInsCache;
  }
  // The page error is captured from INSIDE the wcfSelectAll callback, and
  // TypeScript's control-flow analysis does not observe assignments made in a
  // callback: a `let pageError: SupabaseErrorLike | null = null` still reads as
  // `null` at the check below, so `.message` would not exist on it. A one-slot
  // array is the same capture with a type the checker can follow; `.at(-1)`
  // reproduces the previous last-assignment-wins semantics exactly, and
  // wcfSelectAll stops at the first failing page so at most one is ever pushed.
  const pageErrorSlot: SupabaseErrorLike[] = [];
  const rows = await wcfSelectAll<WeighInRow>(async (f, t) => {
    const res = await sb.from('weigh_ins').select('*').in('session_id', ids).range(f, t);
    if (res.error) pageErrorSlot.push(res.error);
    return res;
  });
  const pageError = pageErrorSlot.at(-1);
  if (pageError) {
    if (throwOnError) throw new Error('loadSheepWeighInsCached weigh_ins: ' + (pageError.message || pageError));
    return _sheepWeighInsCache || rows;
  }
  rows.sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || ''));
  _sheepWeighInsCache = rows;
  _sheepWeighInsCacheAt = Date.now();
  return _sheepWeighInsCache;
}
export function invalidateSheepWeighInsCache(): void {
  _sheepWeighInsCache = null;
  _sheepWeighInsCacheAt = 0;
}
